import { AlertTriangle, BarChart3, Clock3, DollarSign, FileDown, Loader2, RefreshCw, Store } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { MetricCard } from '../components/ui/metric-card'
import { getRevenueSummary, type RevenueBreakdownItem, type RevenueCurrencySummary, type RevenueSummary } from '../lib/api/revenue'

const formatMoney = (amount: number, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'Not checked'
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

const formatTier = (tier: RevenueBreakdownItem['tier']) => {
  if (tier === 'PremiumAnnual') return 'Annual'
  if (tier === 'PremiumMonthly') return 'Monthly'
  return 'Free'
}

const calculatePriceCoverage = (summary: RevenueSummary | null) => {
  const activePaid = summary?.counts.activePaid ?? 0
  if (activePaid === 0) {
    return 0
  }

  return Math.round(((summary?.counts.activePaidWithKnownPrice ?? 0) / activePaid) * 100)
}

export const RevenuePage = () => {
  const [summary, setSummary] = useState<RevenueSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadSummary = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    setError(null)

    try {
      setSummary(await getRevenueSummary())
    } catch (err) {
      console.error('Failed to load revenue summary', err)
      setError('Could not load revenue estimates right now.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  const primaryCurrency = useMemo(() => {
    return summary?.currencies.find((currency) => currency.currency === 'USD') ?? summary?.currencies[0] ?? null
  }, [summary])

  const priceCoverage = calculatePriceCoverage(summary)
  const hasEstimatedRevenue = Boolean(primaryCurrency && primaryCurrency.estimatedMonthlyRevenue > 0)

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">System & Billing</p>
          <h1 className="text-3xl font-semibold text-foreground">Revenue</h1>
          <p className="text-sm text-muted-foreground">
            Store-managed subscription run-rate, separated from settled App Store and Google Play earnings.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 rounded-xl border border-border/70 bg-card/70 px-3 py-2 text-xs text-muted-foreground md:flex">
            <Clock3 className="h-4 w-4" />
            Updated {formatDateTime(summary?.generatedAtUtc)}
          </div>
          <Button variant="outline" onClick={() => void loadSummary(true)} disabled={refreshing}>
            {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </header>

      {loading ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading revenue summary
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-600" />
            <div>
              <p className="font-semibold text-rose-900">Revenue request failed</p>
              <p className="text-sm text-rose-800">{error}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <section className="rounded-lg border border-border bg-card px-5 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {hasEstimatedRevenue ? 'Estimated subscription run-rate' : 'No known-price revenue yet'}
                </p>
                <p className="mt-1 text-2xl font-semibold text-foreground">
                  {primaryCurrency
                    ? `${formatMoney(primaryCurrency.estimatedMonthlyRevenue, primaryCurrency.currency)} / month`
                    : '$0.00 / month'}
                </p>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                  {summary?.estimateBasis}
                </p>
              </div>

              <div className="grid min-w-64 grid-cols-2 gap-3 text-sm">
                <StatusPill label="Paid access" value={summary?.counts.activePaid ?? 0} />
                <StatusPill label="Price coverage" value={`${priceCoverage}%`} />
              </div>
            </div>
          </section>

          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard
              title="Estimated MRR"
              value={primaryCurrency ? formatMoney(primaryCurrency.estimatedMonthlyRevenue, primaryCurrency.currency) : '$0.00'}
              description="Known-price production store subscriptions."
              icon={DollarSign}
              variant={hasEstimatedRevenue ? 'success' : 'warning'}
            />
            <MetricCard
              title="Annual Run-Rate"
              value={primaryCurrency ? formatMoney(primaryCurrency.estimatedAnnualRunRate, primaryCurrency.currency) : '$0.00'}
              description="MRR annualized; not settled proceeds."
              icon={BarChart3}
            />
            <MetricCard
              title="Paid Subscribers"
              value={summary?.counts.activePaid ?? 0}
              description={`${summary?.counts.canceledWithAccess ?? 0} canceled with paid access remaining.`}
              icon={Store}
              variant="accent"
            />
            <MetricCard
              title="Missing Price"
              value={summary?.counts.activePaidMissingPrice ?? 0}
              description="Paid access excluded from revenue totals."
              icon={AlertTriangle}
              variant={(summary?.counts.activePaidMissingPrice ?? 0) > 0 ? 'warning' : 'success'}
            />
          </div>

          {summary && summary.counts.productionStoreManaged === 0 && (
            <Card>
              <CardContent>
                <EmptyState
                  icon={DollarSign}
                  title="No production store subscriptions yet"
                  description="Revenue estimates will appear after production App Store or Google Play subscriptions are verified by the backend."
                />
              </CardContent>
            </Card>
          )}

          <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <BreakdownTable currencies={summary?.currencies ?? []} breakdown={summary?.breakdown ?? []} />
            <AttentionPanel summary={summary} />
          </section>
        </>
      )}
    </div>
  )
}

const StatusPill = ({ label, value }: { label: string; value: string | number }) => (
  <div className="rounded-md border border-border/70 bg-background px-3 py-2">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
  </div>
)

const BreakdownTable = ({
  currencies,
  breakdown
}: {
  currencies: RevenueCurrencySummary[]
  breakdown: RevenueBreakdownItem[]
}) => (
  <section className="space-y-3">
    <div>
      <h2 className="text-lg font-semibold text-foreground">Store run-rate</h2>
      <p className="text-sm text-muted-foreground">Amounts are grouped by currency and are not converted.</p>
    </div>

    {currencies.length > 0 && (
      <div className="grid gap-3 sm:grid-cols-2">
        {currencies.map((currency) => (
          <div key={currency.currency} className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{currency.currency}</p>
            <p className="mt-1 text-xl font-semibold text-foreground">
              {formatMoney(currency.estimatedMonthlyRevenue, currency.currency)} / month
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {currency.knownPriceSubscriptions} known-price, {currency.missingPriceSubscriptions} missing price
            </p>
          </div>
        ))}
      </div>
    )}

    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="grid grid-cols-[1.2fr_0.8fr_0.8fr_1fr] gap-3 border-b border-border bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Plan</span>
        <span>Paid</span>
        <span>Coverage</span>
        <span className="text-right">MRR</span>
      </div>
      {breakdown.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">No production paid subscriptions to estimate yet.</div>
      ) : (
        breakdown.map((item) => (
          <div
            key={`${item.billingSource}-${item.tier}-${item.currency}`}
            className="grid grid-cols-[1.2fr_0.8fr_0.8fr_1fr] gap-3 border-b border-border/70 px-4 py-3 text-sm last:border-b-0"
          >
            <div>
              <p className="font-medium text-foreground">{item.billingSourceLabel}</p>
              <p className="text-xs text-muted-foreground">{formatTier(item.tier)} · {item.currency}</p>
            </div>
            <span className="text-foreground">{item.activePaidSubscriptions}</span>
            <span className="text-muted-foreground">
              {item.knownPriceSubscriptions}/{item.activePaidSubscriptions}
            </span>
            <span className="text-right font-medium text-foreground">
              {formatMoney(item.estimatedMonthlyRevenue, item.currency)}
            </span>
          </div>
        ))
      )}
    </div>
  </section>
)

const AttentionPanel = ({ summary }: { summary: RevenueSummary | null }) => (
  <section className="space-y-3">
    <div>
      <h2 className="text-lg font-semibold text-foreground">What to trust</h2>
      <p className="text-sm text-muted-foreground">This page is an estimate until store financial reports are imported.</p>
    </div>

    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
        <div>
          <p className="font-semibold text-amber-950">Price coverage matters</p>
          <p className="mt-1 text-sm text-amber-900">
            {summary?.counts.activePaidMissingPrice ?? 0} paid subscription(s) have access but no stored price, so they are not included in revenue totals.
          </p>
        </div>
      </div>
    </div>

    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <FileDown className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <p className="font-semibold text-foreground">Actual earnings need store reports</p>
          <p className="mt-1 text-sm text-muted-foreground">
            App Store Connect and Google Play financial reports are still the source for proceeds, refunds, taxes, store commissions, and settled payouts.
          </p>
        </div>
      </div>
    </div>

    <div className="rounded-lg border border-border bg-card p-4">
      <p className="font-semibold text-foreground">Excluded from the estimate</p>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <Detail label="Trials" value={summary?.counts.trial ?? 0} />
        <Detail label="Past due" value={summary?.counts.pastDue ?? 0} />
        <Detail label="Sandbox/fake" value={summary?.counts.sandboxOrFake ?? 0} />
        <Detail label="Refunded" value={summary?.counts.revokedOrRefunded ?? 0} />
      </dl>
    </div>
  </section>
)

const Detail = ({ label, value }: { label: string; value: string | number }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="mt-1 font-semibold text-foreground">{value}</dd>
  </div>
)
